import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { CharacterCounter } from "../../atoms/CharacterCounter";
import {
  HiCode,
  HiViewGrid,
  HiArrowRight,
  HiChevronDown,
  HiSparkles,
} from "react-icons/hi";
import {
  MdAttachFile,
  MdClose,
  MdTextFields,
  MdNumbers,
  MdToggleOn,
  MdCalendarToday,
  MdDataObject,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Select, TextInput, Textarea } from "flowbite-react";
import { Spinner } from "../../atoms/Spinner";
import { toast } from "react-toastify";
import useAuth from "../../../hooks/useAuth";
import { useOpenPrivateFile, usePrivateUploadFile } from "../../../hooks/cdn";
import { useDeleteUserFilesAndDocuments } from "../../../hooks/users";
import parse from "html-react-parser";
import { FaLock } from "react-icons/fa6";
import { UnlockBadge } from "../../atoms/UnlockBadge";
import { PlanTier, usePermissions } from "../../../utils/permissions";
import UpgradeModal from "../../modals/UpgradeModal";
import { PremiumBenefit } from "../../atoms/UpgradePromptContent";
import { CustomButton } from "../../atoms/CustomButton";
import { FieldType, detectFieldType } from "../../../utils/fieldUtils";
import { originalFileName } from "../../../utils/storedFileName";
import NoTranslate from "../../atoms/NoTranslate";

/** The upload hook has already told why the file was refused: no save error on top of it. */
class RefusedUpload extends Error {}

interface Field {
  id: string;
  name: string;
  type: FieldType;
  value: any;
  isExpanded?: boolean;
}

interface VisualJsonBuilderProps {
  initialValue?: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  mode?: "simple" | "advanced";
  onModeChange?: (mode: "simple" | "advanced") => void;
  maxFileLimit?: number;
  onUpgradeClick?: () => void;
  allowedTypes?: FieldType[];
  onSave?: (data: Record<string, any>) => Promise<void>;
  onCancel?: () => void;
  isSaving?: boolean;
  userEndedSubscription?: boolean;
  showActionButtons?: boolean;
  readOnlyFieldName?: boolean;
  /** A new sub-folder of files: saved only once each of its fields holds a file, kept or waiting to be sent. */
  requireFiles?: boolean;
  viewMode?: "files" | "organized";
  preferencesFolder?: string;
  userId?: string;
}

export interface VisualJsonBuilderRef {
  getFilesToDelete: () => string[];
  getFilesToUpload: () => Map<
    string,
    { fieldId: string; file: File; tempId: string }
  >;
  clearUnsavedFiles: () => void;
  clearFilesToDelete: () => void;
  clearFilesToUpload: () => void;
}

export const VisualJsonBuilder = forwardRef<
  VisualJsonBuilderRef,
  VisualJsonBuilderProps
>(
  (
    {
      initialValue = {},
      onChange,
      mode = "simple",
      onModeChange,
      maxFileLimit = 2,
      onUpgradeClick,
      allowedTypes = ["text", "number", "boolean", "date", "object", "file"],
      onSave,
      onCancel,
      isSaving = false,
      userEndedSubscription = false,
      showActionButtons = false,
      readOnlyFieldName = false,
      requireFiles = false,
      viewMode = "organized",
      preferencesFolder,
      userId,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { user, customer_identification } = useAuth();
    const { open: openFile, isOpening: isGetting } = useOpenPrivateFile();
    const { mutate: uploadFileToCDN } = usePrivateUploadFile();
    const { mutate: deleteUserFilesAndDocuments } =
      useDeleteUserFilesAndDocuments();
    const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
      {}
    );
    const [currentFileId, setCurrentFileId] = useState<string | null>(null);
    const [showCodeViewUpgradeModal, setShowCodeViewUpgradeModal] =
      useState(false);
    const [unsavedFiles, setUnsavedFiles] = useState<Set<string>>(new Set());
    const [filesToDelete, setFilesToDelete] = useState<Set<string>>(new Set());
    const [filesToUpload, setFilesToUpload] = useState<
      Map<string, { fieldId: string; file: File; tempId: string }>
    >(new Map());
    const [isProcessingFiles, setIsProcessingFiles] = useState<boolean>(false);

    const permissions = usePermissions(user);
    const largeBusinessTierUser = permissions.isAtLeastTier(
      PlanTier.LARGE_BUSINESS
    );

    const codeViewBenefits: PremiumBenefit[] = [
      {
        id: "code-view-benefit-1",
        text: t("dashboard.upgrade.codeView.benefit1") as string,
      },
      {
        id: "code-view-benefit-2",
        text: t("dashboard.upgrade.codeView.benefit2") as string,
      },
    ];

    const detectType = useCallback(
      (value: any): FieldType => detectFieldType(value, allowedTypes),
      [allowedTypes]
    );

    const [fields, setFields] = useState<Field[]>([]);

    useEffect(() => {
      const initialFields = Object.entries(initialValue).map(([key, value]) => {
        const detectedType = detectType(value);
        const fieldType =
          allowedTypes.length === 1 && (!value || value === "")
            ? allowedTypes[0]
            : detectedType;

        const fieldValue =
          fieldType === "file" ? (Array.isArray(value) ? value : []) : value;

        return {
          id: Math.random().toString(36).substr(2, 9),
          name: key,
          type: fieldType,
          value: fieldValue,
          isExpanded: false,
        };
      });
      setFields(initialFields);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (Object.keys(initialValue).length === 0) return;

      const newKeys = Object.keys(initialValue).sort().join(",");

      if (newKeys) {
        setFields((currentFields) => {
          const hasFieldsBeingEdited = currentFields.some(
            (f) => !f.name || f.name === ""
          );

          if (hasFieldsBeingEdited) {
            return currentFields;
          }

          const currentKeys = currentFields
            .filter((f) => f.name)
            .map((f) => f.name)
            .sort()
            .join(",");

          if (currentKeys !== newKeys) {
            return Object.entries(initialValue).map(([key, value]) => ({
              id: Math.random().toString(36).substr(2, 9),
              name: key,
              type: detectType(value),
              value,
              isExpanded: false,
            }));
          }

          return currentFields;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue, detectType]);

    const updateField = (id: string, updates: Partial<Field>) => {
      const newFields = fields.map((field) =>
        field.id === id ? { ...field, ...updates } : field
      );
      setFields(newFields);
      notifyChange(newFields);
    };

    const handleRemoveSingleFile = (fieldId: string, fileIndex: number) => {
      const field = fields.find((f) => f.id === fieldId);
      const filesArray = Array.isArray(field?.value) ? field.value : [];

      if (filesArray[fileIndex]?.file_name) {
        const fileName = filesArray[fileIndex].file_name;
        const isQueued = filesArray[fileIndex].isQueued === true;

        if (isQueued) {
          setFilesToUpload((prev) => {
            const newMap = new Map(prev);
            newMap.delete(fileName);
            return newMap;
          });

          const updatedFiles = filesArray.filter(
            (_, index) => index !== fileIndex
          );
          updateField(fieldId, { value: updatedFiles });
        } else {
          setFilesToDelete((prev) => new Set(prev).add(fileName));

          setUnsavedFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileName);
            return newSet;
          });
        }
      }
    };

    const notifyChange = (updatedFields: Field[]) => {
      const jsonObject = updatedFields.reduce((acc, field) => {
        if (field.name) {
          let value = field.value;
          if (field.type === "number") value = Number(value) || 0;
          if (field.type === "boolean")
            value = value === true || value === "true";
          if (field.type === "file" && Array.isArray(value)) {
            const filteredFiles = value.filter(
              (file) => !filesToDelete.has(file.file_name)
            );
            value = filteredFiles.map((file) => ({
              file_name: file.file_name,
              extension: file.extension,
              via_sum: true,
              ...(file.ai_engine_doc_id
                ? { ai_engine_doc_id: file.ai_engine_doc_id }
                : {}),
            }));
          }
          acc[field.name] = value;
        }
        return acc;
      }, {} as Record<string, any>);
      onChange(jsonObject);
    };

    const toggleExpand = (id: string) => {
      const newFields = fields.map((field) =>
        field.id === id ? { ...field, isExpanded: !field.isExpanded } : field
      );
      setFields(newFields);
    };

    const handleFileSelected = async (
      fieldId: string,
      files: FileList | null
    ) => {
      if (!files || files.length === 0) return;

      const filesArray = Array.from(files);

      const field = fields.find((f) => f.id === fieldId);
      const existingFiles = Array.isArray(field?.value) ? field.value : [];

      const totalFiles = existingFiles.length + filesArray.length;
      if (totalFiles > maxFileLimit) {
        toast.error(
          t("permissions.businessCard.fileUpload.maxFilesError", {
            max: maxFileLimit,
          })
        );
        return;
      }

      const invalidFiles = filesArray.filter(
        (file) => file.size > 10 * 1048576
      );
      if (invalidFiles.length > 0) {
        toast.error(
          t("permissions.businessCard.fileUpload.fileTooLargeError", {
            maxSizeMB: 10,
          })
        );
        return;
      }

      const queuedFiles: Array<{
        file_name: string;
        extension: string;
        via_sum: boolean;
        isQueued?: boolean;
      }> = [...existingFiles];

      filesArray.forEach((file) => {
        const tempId = `temp_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        const extension = file.name.split(".").pop() || "";

        setFilesToUpload((prev) =>
          new Map(prev).set(tempId, { fieldId, file, tempId })
        );

        queuedFiles.push({
          file_name: tempId,
          extension: extension,
          via_sum: true,
          isQueued: true,
        });
      });

      updateField(fieldId, { value: queuedFiles });

      toast.info(
        t("permissions.businessCard.fileUpload.filesQueued", {
          count: filesArray.length,
        })
      );

      if (fileInputRefs.current[fieldId]) {
        fileInputRefs.current[fieldId]!.value = "";
      }
    };

    const handleViewFile = (fileName: string) => {
      setCurrentFileId(fileName);
      openFile({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        file_name: fileName,
      });
    };

    const handleSave = async () => {
      if (!onSave) return;

      const jsonObject = fields.reduce((acc, field) => {
        if (field.name && field.name.trim() !== "") {
          let value = field.value;
          if (field.type === "number") value = Number(value) || 0;
          if (field.type === "boolean")
            value = value === true || value === "true";
          if (field.type === "file" && Array.isArray(value)) {
            const filteredFiles = value.filter(
              (file) => !filesToDelete.has(file.file_name)
            );
            value = filteredFiles.map((file) => ({
              file_name: file.file_name,
              extension: file.extension,
              via_sum: true,
              ...(file.ai_engine_doc_id
                ? { ai_engine_doc_id: file.ai_engine_doc_id }
                : {}),
            }));
          }
          acc[field.name] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      setIsProcessingFiles(true);

      try {
        if (filesToUpload.size > 0) {
          const tempIdToCdnPathMap: Record<
            string,
            { file_name: string; extension: string }
          > = {};

          for (const [tempId, fileData] of Array.from(
            filesToUpload.entries()
          )) {
            await new Promise<void>((resolve, reject) => {
              uploadFileToCDN(
                {
                  user_identification: customer_identification,
                  entity_identification: user.entity_identification,
                  data: { file: fileData.file } as any,
                },
                {
                  onSuccess: (res: any) => {
                    const cdnPath = res?.data?.element[0]?.file;
                    const extension = res?.data?.element[0]?.extension;
                    if (cdnPath) {
                      tempIdToCdnPathMap[tempId] = {
                        file_name: cdnPath,
                        extension,
                      };
                    }
                    resolve();
                  },
                  onError: () => reject(new RefusedUpload()),
                }
              );
            });
          }

          Object.keys(jsonObject).forEach((fieldKey) => {
            const fieldValue = jsonObject[fieldKey];
            if (
              Array.isArray(fieldValue) &&
              fieldValue.length > 0 &&
              fieldValue[0]?.via_sum
            ) {
              jsonObject[fieldKey] = fieldValue.map((file: any) => {
                if (tempIdToCdnPathMap[file.file_name]) {
                  return {
                    file_name: tempIdToCdnPathMap[file.file_name].file_name,
                    extension: tempIdToCdnPathMap[file.file_name].extension,
                    via_sum: true,
                  };
                }
                return {
                  file_name: file.file_name,
                  extension: file.extension,
                  via_sum: true,
                  ...(file.ai_engine_doc_id
                    ? { ai_engine_doc_id: file.ai_engine_doc_id }
                    : {}),
                };
              });
            }
          });
        }

        if (filesToDelete.size > 0 && preferencesFolder && userId) {
          const filesToDeleteWithMeta = fields.flatMap((field) =>
            field.type === "file" && Array.isArray(field.value)
              ? field.value
                  .filter((file) => filesToDelete.has(file.file_name))
                  .map((file) => ({
                    file_name: file.file_name,
                    ...(file.ai_engine_doc_id
                      ? { ai_engine_doc_id: file.ai_engine_doc_id }
                      : {}),
                  }))
              : []
          );

          await new Promise<void>((resolve, reject) => {
            deleteUserFilesAndDocuments(
              {
                customer_identification,
                entity_identification: user.entity_identification,
                customerId: userId,
                folder: preferencesFolder,
                files: filesToDeleteWithMeta,
              },
              {
                onSuccess: () => resolve(),
                onError: (error) => {
                  console.error("Delete error:", error);
                  reject(error);
                },
              }
            );
          });
        }

        await onSave(jsonObject);

        setUnsavedFiles(new Set());
        setFilesToDelete(new Set());
        setFilesToUpload(new Map());
        setIsProcessingFiles(false);
      } catch (error) {
        console.error("Error during save operation:", error);
        setIsProcessingFiles(false);
        if (!(error instanceof RefusedUpload))
          toast.error(t("dashboard.visualJsonBuilder.saveError"));
      }
    };

    useImperativeHandle(ref, () => ({
      getFilesToDelete: () => Array.from(filesToDelete),
      getFilesToUpload: () => filesToUpload,
      clearUnsavedFiles: () => setUnsavedFiles(new Set()),
      clearFilesToDelete: () => setFilesToDelete(new Set()),
      clearFilesToUpload: () => setFilesToUpload(new Map()),
    }));

    const READ_ONLY_FIELD_CLASS = "bg-gray-100 cursor-not-allowed";

    const readOnlyFieldTheme = (active: boolean) =>
      active
        ? {
            field: {
              input: { withShadow: { off: READ_ONLY_FIELD_CLASS } },
            },
          }
        : undefined;

    const renderFieldInput = (field: Field) => {
      const readOnlyClass = readOnlyFieldName ? READ_ONLY_FIELD_CLASS : "";

      switch (field.type) {
        case "file":
          const filesArray = Array.isArray(field.value) ? field.value : [];
          const canUploadMore = filesArray.length < maxFileLimit;

          return (
            <div className="space-y-2">
              {filesArray.length > 0 && (
                <div className="space-y-2">
                  {filesArray.map((file: any, index: number) => {
                    const isQueued = file.isQueued === true;
                    const isUnsaved = unsavedFiles.has(file.file_name);
                    const isMarkedForDeletion = filesToDelete.has(
                      file.file_name
                    );
                    const isAiEngineDoc = !!file.ai_engine_doc_id;

                    const queuedFileData = isQueued
                      ? filesToUpload.get(file.file_name)
                      : null;
                    const displayName =
                      queuedFileData?.file?.name ||
                      originalFileName(file.file_name || "");

                    return (
                      <div
                        key={index}
                        className={`relative flex items-center gap-2 p-3 rounded-md border-2 transition-all ${
                          isMarkedForDeletion
                            ? "bg-red-50 border-red-400 shadow-sm opacity-75"
                            : isQueued
                            ? "bg-blue-50 border-blue-400 shadow-sm"
                            : isUnsaved
                            ? "bg-amber-50 border-amber-400 shadow-sm"
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        {isMarkedForDeletion && (
                          <div className="absolute -top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full shadow-sm animate-pulse">
                            {t("dashboard.visualJsonBuilder.clickSave")}
                          </div>
                        )}
                        {isQueued && !isMarkedForDeletion && (
                          <div className="absolute -top-2 left-2 px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full shadow-sm animate-pulse">
                            {t("dashboard.visualJsonBuilder.clickSave")}
                          </div>
                        )}
                        {isUnsaved && !isMarkedForDeletion && !isQueued && (
                          <div className="absolute -top-2 left-2 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full shadow-sm animate-pulse">
                            {t("dashboard.visualJsonBuilder.clickSave")}
                          </div>
                        )}

                        <button
                          onClick={() =>
                            !isQueued && handleViewFile(file.file_name)
                          }
                          disabled={
                            isGetting || isMarkedForDeletion || isQueued
                          }
                          className={`flex-1 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed truncate ${
                            isMarkedForDeletion
                              ? "line-through"
                              : isQueued
                              ? "hover:bg-blue-100"
                              : isUnsaved
                              ? "hover:bg-amber-100"
                              : "hover:bg-blue-100"
                          }`}
                        >
                          <MdAttachFile
                            className={`text-lg ${
                              isMarkedForDeletion
                                ? "text-red-700"
                                : isQueued
                                ? "text-blue-700"
                                : isUnsaved
                                ? "text-amber-700"
                                : "text-blue-700"
                            }`}
                          />
                          <div className="flex-1 min-w-0 text-left">
                            <div className="flex items-center gap-2 min-w-0">
                              <p
                                className={`text-sm font-medium shrink-0 ${
                                  isMarkedForDeletion
                                    ? "text-red-900 line-through"
                                    : isQueued
                                    ? "text-blue-900"
                                    : isUnsaved
                                    ? "text-amber-900"
                                    : "text-blue-700"
                                }`}
                              >
                                {isQueued
                                  ? t("dashboard.visualJsonBuilder.queuedFile")
                                  : t("dashboard.visualJsonBuilder.viewFile")}
                              </p>
                              {isAiEngineDoc && (
                                <span
                                  className="inline-flex items-center gap-1 min-w-0 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold"
                                  title={
                                    t(
                                      "dashboard.visualJsonBuilder.aiEngineDocTooltip"
                                    ) as string
                                  }
                                >
                                  <HiSparkles className="w-3 h-3 shrink-0" />
                                  <span className="truncate hidden sm:inline">
                                    {t(
                                      "dashboard.visualJsonBuilder.aiEngineDocBadge"
                                    )}
                                  </span>
                                </span>
                              )}
                            </div>
                            <p
                              className={`text-xs text-gray-600 truncate ${
                                isMarkedForDeletion ? "line-through" : ""
                              }`}
                            >
                              <NoTranslate>{displayName}</NoTranslate>
                            </p>
                            {isUnsaved && !isMarkedForDeletion && !isQueued && (
                              <p className="text-xs font-semibold text-amber-700 mt-0.5">
                                {t(
                                  "dashboard.visualJsonBuilder.fileUploadedNotSavedMessage"
                                )}
                              </p>
                            )}
                          </div>
                          {isGetting && currentFileId === file.file_name && (
                            <Spinner size="sm" />
                          )}
                        </button>
                        {!readOnlyFieldName && (
                          <CustomButton
                            variant="custom"
                            size="icon"
                            onClick={() =>
                              handleRemoveSingleFile(field.id, index)
                            }
                            disabled={isMarkedForDeletion}
                            className="shrink-0 border-red-500 bg-red-50 text-red-700 shadow-none enabled:hover:bg-red-100"
                            aria-label={
                              t(
                                "dashboard.visualJsonBuilder.removeFile"
                              ) as string
                            }
                            icon={
                              <span
                                className="flex"
                                title={
                                  t(
                                    "dashboard.visualJsonBuilder.removeFile"
                                  ) as string
                                }
                              >
                                <MdClose className="w-4 h-4" />
                              </span>
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                  <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <FaLock
                      size={14}
                      className="text-green-600 shrink-0"
                      title="Protected role"
                    />
                    <p className="text-xs text-green-700 leading-relaxed">
                      {parse(t("dashboard.visualJsonBuilder.fileSecurityInfo"))}
                    </p>
                  </div>
                </div>
              )}

              {canUploadMore && !readOnlyFieldName && (
                <div className="flex items-center justify-center p-3 bg-gray-50 border border-gray-300 rounded-md">
                  <button
                    onClick={() => fileInputRefs.current[field.id]?.click()}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {filesArray.length > 0
                      ? t("dashboard.visualJsonBuilder.addMoreFiles", {
                          remaining: maxFileLimit - filesArray.length,
                        })
                      : t("dashboard.visualJsonBuilder.clickToSelectFile", {
                          files: Math.min(10, maxFileLimit - filesArray.length),
                        })}
                  </button>
                </div>
              )}

              {!canUploadMore && onUpgradeClick && (
                <div
                  onClick={onUpgradeClick}
                  className="flex items-center justify-center gap-2 p-3 bg-blue-50 border border-blue-300 rounded-md cursor-pointer hover:bg-blue-100 transition-colors"
                >
                  <span className="text-sm text-blue-700 font-medium">
                    {t("dashboard.visualJsonBuilder.upgradeForMoreFiles")}
                  </span>
                  <UnlockBadge />
                </div>
              )}

              {!canUploadMore && !onUpgradeClick && (
                <div className="flex items-center justify-center p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <span className="text-xs text-amber-700">
                    {t("dashboard.visualJsonBuilder.maxFilesReached")}
                  </span>
                </div>
              )}

              <input
                type="file"
                multiple
                ref={(el) => (fileInputRefs.current[field.id] = el)}
                onChange={(e) => handleFileSelected(field.id, e.target.files)}
                accept=".pdf,.doc,.docx,.rtf,.odt,.txt,.md,.json,.xml,.csv,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.avif,.mp4,.webm,.ogg,.mov,.mkv,.flv,.wmv,.m4v,.3gp,.mpeg,.mp3,.wav,.aac,.m4a,.zip,.rar,.tar"
                className="hidden"
              />
            </div>
          );

        case "object":
          return (
            <div className="space-y-2">
              <button
                onClick={() => toggleExpand(field.id)}
                className="w-full px-3 py-2 bg-blue-50 border border-blue-300 rounded-md text-left flex items-center justify-between hover:bg-blue-100 transition-colors"
              >
                <span className="text-sm font-medium text-blue-900">
                  {Array.isArray(field.value)
                    ? t("dashboard.visualJsonBuilder.arrayItems", {
                        count: field.value.length,
                      })
                    : t("dashboard.visualJsonBuilder.objectFields", {
                        count: Object.keys(field.value || {}).length,
                      })}
                </span>
                <HiChevronDown
                  className={`w-4 h-4 text-blue-600 transition-transform ${
                    field.isExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              {field.isExpanded && (
                <div className="p-3 bg-gray-50 border border-gray-300 rounded-md">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(field.value, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          );

        case "boolean":
          return (
            <label className="flex items-center gap-2 cursor-pointer px-3 py-2 h-[42px]">
              <input
                disabled={readOnlyFieldName}
                type="checkbox"
                checked={field.value === true || field.value === "true"}
                onChange={(e) =>
                  updateField(field.id, { value: e.target.checked })
                }
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                {t("dashboard.visualJsonBuilder.yes")}
              </span>
            </label>
          );

        case "number":
          const numberValue = String(field.value || "");
          const numberCharCount = numberValue.length;
          const isNumberOverLimit = numberCharCount > 20;

          return (
            <div className="space-y-1">
              <NoTranslate>
                <TextInput
                  disabled={readOnlyFieldName}
                  type="number"
                  inputMode="numeric"
                  value={field.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 20) {
                      updateField(field.id, { value });
                    }
                  }}
                  color={isNumberOverLimit ? "failure" : undefined}
                  className="w-full"
                  theme={readOnlyFieldTheme(readOnlyFieldName)}
                  placeholder={
                    t("dashboard.visualJsonBuilder.placeholderNumber") as string
                  }
                />
              </NoTranslate>
              <div className="flex justify-end">
                <span
                  className={`text-xs ${
                    numberCharCount > 280
                      ? "text-amber-600 font-medium"
                      : "text-gray-500"
                  } ${isNumberOverLimit ? "text-red-600 font-bold" : ""}`}
                >
                  {numberCharCount}/20
                </span>
              </div>
            </div>
          );

        case "date":
          return (
            <TextInput
              type="datetime-local"
              value={field.value}
              disabled={readOnlyFieldName}
              onChange={(e) => updateField(field.id, { value: e.target.value })}
              className="w-full"
              theme={readOnlyFieldTheme(readOnlyFieldName)}
            />
          );

        default:
          const textValue = String(field.value || "");
          const textCharCount = textValue.length;
          const isTextOverLimit = textCharCount > 1000;

          return (
            <div className="space-y-1">
              <NoTranslate>
                <Textarea
                  disabled={readOnlyFieldName}
                  rows={4}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 1000) {
                      updateField(field.id, { value });
                    }
                  }}
                  color={isTextOverLimit ? "failure" : undefined}
                  className={`w-full ${readOnlyClass}`}
                  placeholder={
                    t("dashboard.visualJsonBuilder.placeholderText") as string
                  }
                />
              </NoTranslate>
              <CharacterCounter
                currentLength={field.name?.length || 0}
                maxLength={1000}
              />
            </div>
          );
      }
    };

    return (
      <div className="space-y-4">
        {onModeChange && !permissions.isRoleGoverned() && (
          <div className="flex justify-end">
            {mode === "simple" && !largeBusinessTierUser ? (
              <CustomButton
                variant="custom"
                onClick={() => setShowCodeViewUpgradeModal(true)}
                className="group border-blue-200 bg-blue-50 text-blue-700 shadow-none enabled:hover:border-blue-300 enabled:hover:bg-blue-100"
                icon={
                  <HiCode className="w-4 h-4 transition-transform group-hover:scale-110" />
                }
                title={t("dashboard.visualJsonBuilder.switchToCodeView")}
                showUpgrade
              />
            ) : (
              <CustomButton
                variant="custom"
                onClick={() =>
                  onModeChange(mode === "simple" ? "advanced" : "simple")
                }
                className="group border-blue-200 bg-blue-50 text-blue-700 shadow-none enabled:hover:border-blue-300 enabled:hover:bg-blue-100"
                icon={
                  mode === "simple" ? (
                    <HiCode className="w-4 h-4 transition-transform group-hover:scale-110" />
                  ) : (
                    <HiViewGrid className="w-4 h-4 transition-transform group-hover:scale-110" />
                  )
                }
                titleClassName="gap-2"
                title={
                  <>
                    {mode === "simple"
                      ? t("dashboard.visualJsonBuilder.switchToCodeView")
                      : t("dashboard.visualJsonBuilder.switchToSimpleView")}
                    <HiArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                  </>
                }
              />
            )}
          </div>
        )}

        <div className="space-y-4">
          {fields.map((field, index) => {
            const getTypeColor = () => {
              switch (field.type) {
                case "text":
                  return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    iconColor: "text-blue-600",
                    badge: "bg-blue-100 text-blue-700",
                    Icon: MdTextFields,
                  };
                case "number":
                  return {
                    bg: "bg-green-50",
                    border: "border-green-200",
                    iconColor: "text-green-600",
                    badge: "bg-green-100 text-green-700",
                    Icon: MdNumbers,
                  };
                case "boolean":
                  return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    iconColor: "text-blue-600",
                    badge: "bg-blue-100 text-blue-700",
                    Icon: MdToggleOn,
                  };
                case "date":
                  return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    iconColor: "text-blue-600",
                    badge: "bg-blue-100 text-blue-700",
                    Icon: MdCalendarToday,
                  };
                case "file":
                  return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    iconColor: "text-blue-600",
                    badge: "bg-blue-100 text-blue-700",
                    Icon: MdAttachFile,
                  };
                case "object":
                  return {
                    bg: "bg-amber-50",
                    border: "border-amber-200",
                    iconColor: "text-amber-600",
                    badge: "bg-amber-100 text-amber-700",
                    Icon: MdDataObject,
                  };
                default:
                  return {
                    bg: "bg-gray-50",
                    border: "border-gray-200",
                    iconColor: "text-gray-600",
                    badge: "bg-gray-100 text-gray-700",
                    Icon: MdTextFields,
                  };
              }
            };

            const typeColors = getTypeColor();

            return (
              <div
                key={field.id}
                className={`relative bg-white border-2 ${typeColors.border} rounded-lg p-5 shadow-sm hover:shadow-sm transition-all`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-2 ${typeColors.badge}`}
                    >
                      <typeColors.Icon className="w-4 h-4" />
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                      <label className="shrink-0 text-sm font-medium text-gray-700">
                        {t("dashboard.visualJsonBuilder.fieldName")}:
                      </label>
                      <TextInput
                        type="text"
                        value={field.name}
                        onChange={(e) => {
                          if (readOnlyFieldName) return;
                          const value = e.target.value;
                          if (value.length <= 320) {
                            updateField(field.id, { name: value });
                          }
                        }}
                        readOnly={readOnlyFieldName}
                        className="flex-1 min-w-0"
                        theme={readOnlyFieldTheme(readOnlyFieldName)}
                        placeholder={
                          t(
                            "dashboard.visualJsonBuilder.placeholderFieldName"
                          ) as string
                        }
                        maxLength={320}
                      />
                    </div>
                    {!readOnlyFieldName && (
                      <div className="flex justify-end">
                        <CharacterCounter
                          currentLength={field.name?.length || 0}
                          maxLength={320}
                        />
                      </div>
                    )}
                  </div>

                  {allowedTypes.length > 1 && !readOnlyFieldName && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <label className="shrink-0 text-sm font-medium text-gray-700">
                        {t("dashboard.visualJsonBuilder.type")}:
                      </label>
                      <Select
                        value={field.type}
                        onChange={(e) => {
                          const newType = e.target.value as FieldType;
                          updateField(field.id, {
                            type: newType,
                            value: "",
                          });
                        }}
                        className="flex-1 min-w-0"
                      >
                        {allowedTypes.includes("text") && (
                          <option value="text">
                            {t("dashboard.visualJsonBuilder.typeText")}
                          </option>
                        )}
                        {allowedTypes.includes("number") && (
                          <option value="number">
                            {t("dashboard.visualJsonBuilder.typeNumber")}
                          </option>
                        )}
                        {allowedTypes.includes("boolean") && (
                          <option value="boolean">
                            {t("dashboard.visualJsonBuilder.typeBoolean")}
                          </option>
                        )}
                        {allowedTypes.includes("date") && (
                          <option value="date">
                            {t("dashboard.visualJsonBuilder.typeDate")}
                          </option>
                        )}
                        {allowedTypes.includes("object") && (
                          <option value="object">
                            {t("dashboard.visualJsonBuilder.typeObject")}
                          </option>
                        )}
                        {allowedTypes.includes("file") && (
                          <option value="file">
                            {t("dashboard.visualJsonBuilder.typeFile")}
                          </option>
                        )}
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {viewMode === "files"
                        ? t("dashboard.visualJsonBuilder.files")
                        : t("dashboard.visualJsonBuilder.value")}
                      :
                    </label>
                    <div
                      className={`${typeColors.bg} rounded-lg p-3 border ${typeColors.border}`}
                    >
                      {renderFieldInput(field)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showActionButtons && (
          <div className="flex justify-end mt-4 gap-2">
            <CustomButton
              variant="secondary"
              onClick={onCancel}
              title={t("common.cancel")}
            />
            <CustomButton
              onClick={handleSave}
              disabled={
                isSaving ||
                isProcessingFiles ||
                userEndedSubscription ||
                fields.length === 0 ||
                (requireFiles &&
                  fields.some(
                    (field) =>
                      field.type === "file" &&
                      (!Array.isArray(field.value) || field.value.length === 0)
                  )) ||
                readOnlyFieldName
              }
              isLoading={isSaving || isProcessingFiles}
              variant="primary"
              icon="save"
              title={t("common.saveShort") as string}
            />
          </div>
        )}

        <UpgradeModal
          showModal={showCodeViewUpgradeModal}
          setShowModal={setShowCodeViewUpgradeModal}
          targetPlan={PlanTier.LARGE_BUSINESS}
          title={t("dashboard.upgrade.codeView.title") as string}
          description={t("dashboard.upgrade.codeView.description") as string}
          benefits={codeViewBenefits}
          icon={HiCode}
          iconTheme="blue"
          upgradeText={t("dashboard.upgrade.key3") as string}
        />
      </div>
    );
  }
);
